import { Loader2 } from "lucide-react";
import { type ReactNode, useState } from "react";

import type { AdminApi } from "@/api";
import { Button, Input } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { ReplayCommit, TeBaseline, TeSpec } from "@/types";
import {
  FIXED_CONDITIONS,
  modelLabel,
  presets,
  shortSha,
  specAxes,
  specKey,
  teName,
  teSub,
} from "./teSpec";

function todayLocal() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/**
 * The TE, as a diff from the dev deployment (new artifact, "2 · TE 선택").
 *
 * Every axis defaults to "배포본과 같음", and the TE is only what was moved
 * off it — so the name writes itself and two people get the same TE. Prompt
 * versions are shown but locked: Replay Lab resolves prompts from the code's
 * snapshot time alone and cannot pin them (BE-REPLAY-PROMPT-PIN). The things
 * no request can carry stay on screen too, folded — they are where to look
 * when a score will not reproduce.
 */
export function TeEditor({
  api,
  baseline,
  spec,
  frozen,
  commitSource,
  onChange,
  status = null,
}: {
  api: AdminApi;
  baseline: TeBaseline | null;
  spec: TeSpec;
  frozen: boolean;
  /** Where to list commits from — the page's first TC. Null = no picker. */
  commitSource: { environment: string; feedbackId: string } | null;
  onChange: (next: TeSpec) => void;
  /** Whether this page ran this TE already, and its mean — the host knows. */
  status?: string | null;
}) {
  const axes = specAxes(spec, baseline);
  const set = (patch: Partial<TeSpec>) => {
    const next: TeSpec = { ...spec, ...patch };
    for (const key of Object.keys(next) as (keyof TeSpec)[]) {
      if (next[key] === undefined) delete next[key];
    }
    onChange(next);
  };

  return (
    <div className="grid gap-3">
      <p className="rounded-md bg-surface-subtle px-3 py-2 text-xs text-muted-foreground">
        기준 = dev 배포본 · {baseline?.deployed_sha ? shortSha(baseline.deployed_sha) : "sha 미상"} ·{" "}
        {modelLabel(baseline?.default_model, baseline)} · 프롬프트는 코드 시각의 활성 버전
      </p>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[11px] text-muted-foreground">자주 쓰는 조합</span>
        {presets(baseline).map((preset) => {
          const on = specKey(preset.spec) === specKey(spec);
          return (
            <button
              key={preset.label}
              type="button"
              disabled={frozen}
              aria-pressed={on}
              onClick={() => onChange({ ...preset.spec })}
              className={cn(
                "rounded-full border px-2.5 py-0.5 text-[11px] transition disabled:opacity-50",
                on ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted",
              )}
            >
              {preset.label}
            </button>
          );
        })}
      </div>

      <div className="grid gap-2">
        <AxisRow label="모델" changed={Boolean(spec.model)}>
          <select
            aria-label="모델"
            disabled={frozen}
            value={spec.model ?? ""}
            onChange={(e) => set({ model: e.target.value || undefined })}
            className="w-full rounded-md border border-border bg-surface-input px-2 py-1.5 text-[13px] disabled:opacity-60"
          >
            <option value="">배포본과 같음 — {modelLabel(baseline?.default_model, baseline)}</option>
            {(baseline?.models ?? [])
              .filter((m) => m.id !== baseline?.default_model)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label} · {m.provider_label}
                </option>
              ))}
          </select>
        </AxisRow>
        {(["Main Agent 프롬프트", "Subagent 프롬프트"] as const).map((label) => (
          <AxisRow key={label} label={label} changed={false}>
            <select
              aria-label={label}
              disabled
              value=""
              title="Replay Lab 은 프롬프트를 코드 시각(snapshot_at)으로만 정합니다 — BE-REPLAY-PROMPT-PIN"
              className="w-full rounded-md border border-dashed border-border bg-transparent px-2 py-1.5 text-[13px] text-muted-foreground"
            >
              <option value="">배포본 버전으로 고정 — Replay Lab 확장 대기</option>
            </select>
          </AxisRow>
        ))}
      </div>

      <details className="rounded-md border border-border">
        <summary className="cursor-pointer px-3 py-2 text-xs text-muted-foreground">
          코드 · 스냅샷 · 재현 입력 (기본: 배포본)
        </summary>
        <div className="grid gap-2 border-t border-border px-3 py-3">
          <AxisRow label="코드 · 스냅샷" changed={Boolean(spec.code)}>
            <CodeAxis
              api={api}
              frozen={frozen}
              spec={spec}
              baseline={baseline}
              source={commitSource}
              onPick={(code) => set({ code })}
            />
          </AxisRow>
          <AxisRow label="history_limit" changed={spec.history != null}>
            <select
              aria-label="history_limit"
              disabled={frozen}
              value={spec.history == null ? "" : String(spec.history)}
              onChange={(e) => set({ history: e.target.value ? Number(e.target.value) : undefined })}
              className="w-full rounded-md border border-border bg-surface-input px-2 py-1.5 text-[13px] disabled:opacity-60"
            >
              <option value="">배포본과 같음 — {baseline?.history_limit ?? 50}</option>
              <option value="10">10 · 최근 10 턴만</option>
              <option value="5">5 · 최근 5 턴만</option>
            </select>
          </AxisRow>
          <AxisRow label="force_response" changed={spec.force != null}>
            <select
              aria-label="force_response"
              disabled={frozen}
              value={spec.force == null ? "" : String(spec.force)}
              onChange={(e) => set({ force: e.target.value ? e.target.value === "true" : undefined })}
              className="w-full rounded-md border border-border bg-surface-input px-2 py-1.5 text-[13px] disabled:opacity-60"
            >
              <option value="">배포본과 같음 — {String(baseline?.force_response ?? false)}</option>
              <option value="true">true · 침묵 판단을 건너뛴다</option>
            </select>
          </AxisRow>
        </div>
      </details>

      <details className="rounded-md border border-border">
        <summary className="cursor-pointer px-3 py-2 text-xs text-muted-foreground">
          바꿀 수 없는 조건 {FIXED_CONDITIONS.length} 개 (재현 요청에 실을 수 없음)
        </summary>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 border-t border-border px-3 py-3 text-xs">
          {FIXED_CONDITIONS.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      </details>

      {/* The TE being built: its name, how it differs, one chip per
          changed axis, and whether this page has run it (artifact). */}
      <div className="grid gap-1 rounded-md border border-border px-3 py-2">
        <span className="text-[13px] font-semibold">{teName(spec, baseline)}</span>
        <span className="text-xs text-muted-foreground">{teSub(spec, baseline)}</span>
        {axes.length > 0 && (
          <span className="flex flex-wrap gap-1">
            {axes.map((axis) => (
              <span
                key={axis.label}
                className="rounded-full border border-primary/40 px-1.5 text-[10px] text-primary"
              >
                {axis.label} {axis.to}
              </span>
            ))}
          </span>
        )}
        {status && <span className="text-xs text-muted-foreground">{status}</span>}
      </div>
      <p
        className={cn(
          "rounded-md border px-3 py-2 text-xs",
          axes.length > 1
            ? "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
            : "border-border bg-surface-subtle text-muted-foreground",
        )}
      >
        {axes.length === 0
          ? "바꾼 것이 없습니다 — 배포본 그대로 다시 돌립니다. 같은 조건에서 점수가 흔들리는지(재현성) 볼 때 씁니다."
          : axes.length === 1
            ? "1 개 항목만 다릅니다 — 점수가 움직이면 원인이 분명합니다."
            : `${axes.length} 개 항목이 동시에 다릅니다. 점수가 움직여도 어느 쪽 때문인지 가릴 수 없습니다. 한 번에 하나씩 바꾸는 편이 읽힙니다.`}
      </p>
    </div>
  );
}

function AxisRow({ label, changed, children }: { label: string; changed: boolean; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_minmax(0,1fr)_40px] items-center gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
      <span className={cn("text-[10px]", changed ? "font-medium text-primary" : "text-transparent")}>
        변경됨
      </span>
    </div>
  );
}

/**
 * Code is picked as a commit on a date, the same way Negative Reviews does —
 * the commit's time becomes `snapshot_at`, which also selects the prompts
 * active then. The list comes from Replay Lab, which authorises it through a
 * 👎, so the page's first TC stands in.
 */
function CodeAxis({
  api,
  frozen,
  spec,
  baseline,
  source,
  onPick,
}: {
  api: AdminApi;
  frozen: boolean;
  spec: TeSpec;
  baseline: TeBaseline | null;
  source: { environment: string; feedbackId: string } | null;
  onPick: (code: TeSpec["code"]) => void;
}) {
  const [date, setDate] = useState(todayLocal);
  const [commits, setCommits] = useState<ReplayCommit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    if (!source) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.listReplayCommits(source.environment, source.feedbackId, date);
      setCommits(res.commits);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setCommits(null);
    } finally {
      setLoading(false);
    }
  };

  if (spec.code) {
    return (
      <div className="flex min-w-0 items-center gap-2 text-[13px]">
        <span className="min-w-0 truncate" title={spec.code.subject}>
          {shortSha(spec.code.git_ref)} · {spec.code.code_resolution}
          {spec.code.subject ? ` · ${spec.code.subject}` : ""}
        </span>
        <Button variant="outline" size="sm" disabled={frozen} onClick={() => onPick(undefined)}>
          배포본으로
        </Button>
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-2 text-[13px]">
        <span className="text-muted-foreground">
          배포본과 같음 — {baseline?.deployed_sha ? shortSha(baseline.deployed_sha) : "배포본"}
        </span>
        <Input
          type="date"
          aria-label="커밋 날짜"
          value={date}
          disabled={frozen || !source}
          onChange={(e) => setDate(e.target.value)}
          className="w-40 py-1"
        />
        <Button variant="outline" size="sm" disabled={frozen || !source || loading} onClick={() => void load()}>
          {loading && <Loader2 className="animate-spin" />}커밋 보기
        </Button>
      </div>
      {!source && (
        <span className="text-[11px] text-muted-foreground">TC 를 고정하면 커밋을 고를 수 있습니다.</span>
      )}
      {error && <span className="text-[11px] text-destructive">{error}</span>}
      {commits && commits.length === 0 && (
        <span className="text-[11px] text-muted-foreground">이 날짜의 커밋이 없습니다.</span>
      )}
      {commits && commits.length > 0 && (
        <ul className="max-h-48 overflow-y-auto rounded-md border border-border">
          {commits.map((commit) => (
            <li key={commit.sha}>
              <button
                type="button"
                disabled={frozen}
                onClick={() =>
                  onPick({
                    git_ref: commit.sha,
                    code_resolution: "commit-time",
                    snapshot_at: commit.committed_at,
                    subject: commit.subject,
                  })
                }
                className="grid w-full gap-0.5 border-b border-border px-3 py-1.5 text-left last:border-b-0 hover:bg-muted"
              >
                <span className="truncate text-[13px]">{commit.subject}</span>
                <span className="text-[11px] text-muted-foreground">
                  {commit.short_sha} · {commit.committed_at.slice(11, 16)} · {commit.author}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
